import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  ContentCopyOutlined,
  DeleteOutline,
  EditOutlined,
  PeopleAltOutlined,
  AdminPanelSettingsOutlined,
  AccessTimeOutlined,
  RefreshOutlined,
  DownloadOutlined,
  SearchOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import AuthShell from '../components/AuthShell';
import { AuthAPI } from '../api/auth';

const PAGE_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard', required: true },
  { key: 'assets', label: 'Assets' },
  { key: 'monitor', label: 'Monitor' },
  { key: 'cmdb', label: 'CMDB' },
  { key: 'reports', label: 'Reports' },
  { key: 'network_scan', label: 'Agentless Scan' },
  { key: 'connectors', label: 'Credential Vault' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'cloud', label: 'Cloud Connections' },
];

function normalizePageAccess(pageAccess) {
  const set = new Set(Array.isArray(pageAccess) ? pageAccess.map((value) => String(value || '').trim()).filter(Boolean) : []);
  set.add('dashboard');
  return Array.from(set);
}

function pageAccessLabel(key) {
  return PAGE_OPTIONS.find((item) => item.key === key)?.label || String(key || '');
}

function formatDate(value) {
  if (!value) return 'Never';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'Never';
  return dt.toLocaleString();
}

function csvEscape(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function StatCard({ label, value, hint, icon, color = '#2563eb' }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.2,
        borderRadius: 2,
        borderColor: '#dbe7f5',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
        flex: 1,
        minWidth: 0,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700, mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 800, lineHeight: 1 }}>
            {value}
          </Typography>
          {hint ? (
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.8 }}>
              {hint}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: `${color}14`,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

const compactIconButtonSx = {
  width: 34,
  height: 34,
  borderRadius: 1.5,
  border: '1px solid #dbe2ea',
  backgroundColor: '#fff',
};

export default function UserRoleManagementPage() {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);
  const [roles, setRoles] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState('all');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [roleDialogOpen, setRoleDialogOpen] = React.useState(false);
  const [roleDialogMode, setRoleDialogMode] = React.useState('create');
  const [editingRole, setEditingRole] = React.useState(null);
  const [roleForm, setRoleForm] = React.useState({ name: '', app_scope: 'itam', page_access: ['dashboard'] });
  const [userDialogOpen, setUserDialogOpen] = React.useState(false);
  const [userDialogMode, setUserDialogMode] = React.useState('create');
  const [editingUser, setEditingUser] = React.useState(null);
  const [userForm, setUserForm] = React.useState({ full_name: '', employee_id: '', contact_number: '', email: '', role: '' });
  const [deleteRoleTarget, setDeleteRoleTarget] = React.useState(null);
  const [deleteUserTarget, setDeleteUserTarget] = React.useState(null);
  const [credentialDialogOpen, setCredentialDialogOpen] = React.useState(false);
  const [createdCredentials, setCreatedCredentials] = React.useState(null);
  const [snack, setSnack] = React.useState({ open: false, severity: 'info', message: '' });

  const openSnack = React.useCallback((message, severity = 'info') => {
    setSnack({ open: true, severity, message });
  }, []);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, usersRes] = await Promise.allSettled([
        AuthAPI.getEnterpriseRoles(),
        AuthAPI.getEnterpriseRoleUsers(),
      ]);

      if (rolesRes.status === 'fulfilled') {
        setRoles(Array.isArray(rolesRes.value?.data?.data) ? rolesRes.value.data.data : []);
      }
      if (usersRes.status === 'fulfilled') {
        const data = usersRes.value?.data?.data || usersRes.value?.data?.users || [];
        setUsers(Array.isArray(data) ? data : []);
      }
      if (rolesRes.status === 'rejected' && usersRes.status === 'rejected') {
        openSnack('Unable to load role management data right now.', 'error');
      }
    } catch {
      openSnack('Unable to load role management data right now.', 'error');
    } finally {
      setLoading(false);
    }
  }, [openSnack]);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  const activeUsers24h = React.useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return users.filter((user) => {
      if (!user.last_login_at) return false;
      const ts = Date.parse(user.last_login_at);
      return !Number.isNaN(ts) && ts >= cutoff;
    }).length;
  }, [users]);

  const filteredUsers = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesSearch =
        !query ||
        user.full_name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.role?.toLowerCase().includes(query);
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [search, users, roleFilter]);

  const pagedUsers = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const filteredRoles = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return roles.filter((role) => {
      return (
        !query ||
        role.name?.toLowerCase().includes(query) ||
        String(role.enterprise_id || '').includes(query) ||
        role.created_by_user_name?.toLowerCase().includes(query) ||
        role.created_by_user_email?.toLowerCase().includes(query)
      );
    });
  }, [roles, search]);

  const totalRoles = roles.length;
  const roleNames = React.useMemo(() => roles.map((role) => role.name).filter(Boolean), [roles]);

  const openRoleDialog = (mode, role = null) => {
    setRoleDialogMode(mode);
    setEditingRole(role);
    setRoleForm({
      name: role?.name || '',
      app_scope: role?.app_scope || 'itam',
      page_access: normalizePageAccess(role?.page_access || ['dashboard']),
    });
    setRoleDialogOpen(true);
  };

  const closeRoleDialog = () => {
    setRoleDialogOpen(false);
    setEditingRole(null);
    setRoleForm({ name: '', app_scope: 'itam', page_access: ['dashboard'] });
  };

  const openUserDialog = (mode, user = null) => {
    setUserDialogMode(mode);
    setEditingUser(user);
    setUserForm({
      full_name: user?.full_name || '',
      employee_id: user?.employee_id || '',
      contact_number: user?.contact_number || '',
      email: user?.email || '',
      role: user?.role || roles?.[0]?.name || '',
    });
    setUserDialogOpen(true);
  };

  const closeUserDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
    setUserForm({ full_name: '', employee_id: '', contact_number: '', email: '', role: roles?.[0]?.name || '' });
  };

  const toggleRoleAccess = (key) => {
    if (key === 'dashboard') return;
    setRoleForm((prev) => {
      const next = new Set(prev.page_access || []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      next.add('dashboard');
      return { ...prev, page_access: Array.from(next) };
    });
  };

  const handleRoleScopeChange = (scope) => {
    setRoleForm((prev) => ({
      ...prev,
      app_scope: scope,
      page_access: scope === 'helpdesk' ? [] : normalizePageAccess(prev.page_access || ['dashboard']),
    }));
  };

  const handleSaveRole = async (event) => {
    event.preventDefault();
    const payload = {
      name: roleForm.name.trim(),
      app_scope: roleForm.app_scope,
      page_access: roleForm.app_scope === 'helpdesk' ? [] : normalizePageAccess(roleForm.page_access),
    };
    if (!payload.name) {
      openSnack('Role name is required.', 'warning');
      return;
    }

    try {
      setSaving(true);
      if (roleDialogMode === 'edit' && editingRole) {
        await AuthAPI.updateEnterpriseRole(editingRole.id, payload);
        openSnack('Role updated successfully.', 'success');
      } else {
        await AuthAPI.createEnterpriseRole(payload);
        openSnack('Role created successfully.', 'success');
      }
      closeRoleDialog();
      await loadData();
    } catch (error) {
      openSnack(error?.response?.data?.message || error?.response?.data?.error || 'Unable to save role', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUser = async (event) => {
    event.preventDefault();
    const payload = {
      full_name: userForm.full_name.trim(),
      employee_id: userForm.employee_id.trim(),
      contact_number: userForm.contact_number.trim(),
      email: userForm.email.trim(),
      role: userForm.role,
    };
    if (!payload.full_name || !payload.email || !payload.role) {
      openSnack('Name, email, and role are required.', 'warning');
      return;
    }

    try {
      setSaving(true);
      if (userDialogMode === 'edit' && editingUser) {
        await AuthAPI.updateEnterpriseRoleUser(editingUser.id, payload);
        openSnack('User updated successfully.', 'success');
      } else {
        const res = await AuthAPI.createEnterpriseRoleUser(payload);
        const credentials = res?.data?.credentials || null;
        setCreatedCredentials(credentials);
        setCredentialDialogOpen(Boolean(credentials?.password));
        openSnack('User created successfully.', 'success');
      }
      closeUserDialog();
      await loadData();
    } catch (error) {
      openSnack(error?.response?.data?.message || error?.response?.data?.error || 'Unable to save user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteRoleTarget) return;
    try {
      setSaving(true);
      await AuthAPI.deleteEnterpriseRole(deleteRoleTarget.id);
      openSnack('Role deleted successfully.', 'success');
      setDeleteRoleTarget(null);
      await loadData();
    } catch (error) {
      openSnack(error?.response?.data?.message || error?.response?.data?.error || 'Unable to delete role', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget) return;
    try {
      setSaving(true);
      await AuthAPI.deleteEnterpriseRoleUser(deleteUserTarget.id);
      openSnack('User deleted successfully.', 'success');
      setDeleteUserTarget(null);
      await loadData();
    } catch (error) {
      openSnack(error?.response?.data?.message || error?.response?.data?.error || 'Unable to delete user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ['Name', 'Employee ID', 'Contact Number', 'Email', 'Role', 'Last Login', 'Email Verified'],
      ...filteredUsers.map((user) => [
        user.full_name || '',
        user.employee_id || '',
        user.contact_number || '',
        user.email || '',
        user.role || '',
        formatDate(user.last_login_at),
        user.email_verified ? 'Yes' : 'No',
      ]),
    ];
    const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auth-role-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  React.useEffect(() => {
    setPage(0);
  }, [search, roleFilter]);

  React.useEffect(() => {
    if (!userDialogOpen && roles.length && !userForm.role) {
      setUserForm((prev) => ({ ...prev, role: roles[0].name }));
    }
  }, [roles, userDialogOpen, userForm.role]);

  return (
    <AuthShell
      title="User & Role Management"
      subtitle="Create reusable roles, manage users, and keep auth access centralized."
      topBrandImageSrc="/site.png"
    >
      <Stack spacing={3}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2.25, sm: 2.75, md: 3 },
            borderRadius: 2,
            borderColor: '#dbe7f5',
            backgroundColor: '#fff',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 800, color: '#0f172a' }}>
                User & Role Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.75, lineHeight: 1.7 }}>
                Centralize enterprise user creation and reusable role templates in one secure workspace.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshOutlined />}
              onClick={loadData}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Refresh Data
            </Button>
          </Stack>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <StatCard
              label="Total Users"
              value={users.length}
              hint="Role-based users in this enterprise"
              icon={<PeopleAltOutlined fontSize="small" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              label="Active in 24h"
              value={activeUsers24h}
              hint="Based on last login"
              icon={<AccessTimeOutlined fontSize="small" />}
              color="#16a34a"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              label="Total Roles"
              value={totalRoles}
              hint={roleNames.length ? roleNames.join(', ') : 'No roles found'}
              icon={<AdminPanelSettingsOutlined fontSize="small" />}
              color="#7c3aed"
            />
          </Grid>
        </Grid>

        <Paper
          variant="outlined"
          sx={{
            p: 1,
            borderRadius: 2,
            borderColor: '#dbe7f5',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, next) => setActiveTab(next)}
            sx={{
              minHeight: 44,
              '& .MuiTabs-indicator': { height: 3, borderRadius: 99 },
            }}
          >
            <Tab label="Users" sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44 }} />
            <Tab label="Roles" sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44 }} />
          </Tabs>
        </Paper>

        {activeTab === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: '#dbe7f5',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    Users
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Add users, assign a reusable role, and manage access.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadOutlined />}
                    onClick={exportCsv}
                    disabled={!filteredUsers.length}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddOutlined />}
                    onClick={() => openUserDialog('create')}
                    disabled={!roles.length}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Add User
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ p: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search users"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: '100%', sm: 260 }, flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                <InputLabel>Role filter</InputLabel>
                <Select
                  label="Role filter"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="all">All roles</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.name}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Employee ID</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Contact Number</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Last Login</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 140 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#64748b' }}>
                        Loading users...
                      </TableCell>
                    </TableRow>
                  ) : pagedUsers.length ? (
                    pagedUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{user.full_name || '-'}</TableCell>
                        <TableCell>{user.employee_id || '-'}</TableCell>
                        <TableCell>{user.contact_number || '-'}</TableCell>
                        <TableCell>{user.email || '-'}</TableCell>
                        <TableCell>
                          <Chip label={user.role || '-'} size="small" variant="outlined" color="primary" />
                        </TableCell>
                        <TableCell>{formatDate(user.last_login_at)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Edit user">
                              <IconButton size="small" onClick={() => openUserDialog('edit', user)} sx={compactIconButtonSx}>
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete user">
                              <IconButton size="small" color="error" onClick={() => setDeleteUserTarget(user)} sx={compactIconButtonSx}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                      <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 7 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {users.length ? 'No users match the current filters.' : 'No users added yet.'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                          {users.length ? 'Try clearing filters.' : 'Create a role first, then add users against it.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: '#dbe7f5',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    Roles
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Define access once and reuse it while adding users.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddOutlined />}
                  onClick={() => openRoleDialog('create')}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Add Role
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ p: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search roles"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: '100%', sm: 260 }, flex: 1 }}
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Workspace</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Enterprise ID</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Page Access</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Users</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 140 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#64748b' }}>
                        Loading roles...
                      </TableCell>
                    </TableRow>
                  ) : filteredRoles.length ? (
                    filteredRoles.map((role) => (
                      <TableRow key={role.id} hover>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{role.name}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={role.app_scope === 'helpdesk' ? 'Helpdesk' : 'ITAM'}
                            variant="outlined"
                            color={role.app_scope === 'helpdesk' ? 'secondary' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>{role.enterprise_id || '-'}</TableCell>
                        <TableCell>
                          {role.app_scope === 'helpdesk' ? (
                            <Chip size="small" label="Not used" variant="outlined" />
                          ) : (
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              {(role.page_access || []).slice(0, 4).map((key) => (
                                <Chip key={key} size="small" label={pageAccessLabel(key)} />
                              ))}
                              {(role.page_access || []).length > 4 ? (
                                <Chip size="small" label={`+${role.page_access.length - 4}`} variant="outlined" />
                              ) : null}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                              {role.created_by_user_name || '-'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {role.created_by_user_email || (role.created_by_user_id ? `User ID ${role.created_by_user_id}` : '-')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={role.role_user_count || 0} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Edit role">
                              <IconButton size="small" onClick={() => openRoleDialog('edit', role)} sx={compactIconButtonSx}>
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete role">
                              <IconButton size="small" color="error" onClick={() => setDeleteRoleTarget(role)} sx={compactIconButtonSx}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 7 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          No roles yet.
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                          Add one first so users can be assigned cleanly.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>

      <Dialog open={roleDialogOpen} onClose={closeRoleDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {roleDialogMode === 'edit' ? 'Edit Role' : 'Add Role'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Define access once, then reuse the role while adding users.
              </Typography>
            </Box>
            <IconButton onClick={closeRoleDialog} size="small">
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box component="form" id="role-form" onSubmit={handleSaveRole} sx={{ pt: 1 }}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Role name"
                value={roleForm.name}
                onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
              <FormControl fullWidth>
                <InputLabel id="role-app-scope-label">Workspace</InputLabel>
                <Select
                  labelId="role-app-scope-label"
                  label="Workspace"
                  value={roleForm.app_scope || 'itam'}
                  onChange={(e) => handleRoleScopeChange(e.target.value)}
                >
                  <MenuItem value="itam">ITAM</MenuItem>
                  <MenuItem value="helpdesk">Helpdesk</MenuItem>
                </Select>
              </FormControl>
              {roleForm.app_scope !== 'helpdesk' ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Page access
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {PAGE_OPTIONS.map((option) => {
                      const selected = Array.isArray(roleForm.page_access) && roleForm.page_access.includes(option.key);
                      return (
                        <Chip
                          key={option.key}
                          label={option.required ? `${option.label} (required)` : option.label}
                          variant={selected ? 'filled' : 'outlined'}
                          color={selected ? 'primary' : 'default'}
                          disabled={option.required}
                          clickable={!option.required}
                          onClick={() => toggleRoleAccess(option.key)}
                          sx={{ borderRadius: 999, fontWeight: 600 }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ) : (
                <Alert severity="info">Helpdesk roles only need a role name and workspace selection.</Alert>
              )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeRoleDialog} variant="text" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="role-form"
            variant="contained"
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 700, minWidth: 140 }}
          >
            {saving ? 'Saving...' : roleDialogMode === 'edit' ? 'Save Changes' : 'Add Role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={userDialogOpen} onClose={closeUserDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {userDialogMode === 'edit' ? 'Edit User' : 'Add User'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Select a role to inherit its access.
              </Typography>
            </Box>
            <IconButton onClick={closeUserDialog} size="small">
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box component="form" id="user-form" onSubmit={handleSaveUser} sx={{ pt: 1 }}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Name"
                value={userForm.full_name}
                onChange={(e) => setUserForm((prev) => ({ ...prev, full_name: e.target.value }))}
                required
              />
              <TextField
                fullWidth
                label="Employee ID"
                value={userForm.employee_id}
                onChange={(e) => setUserForm((prev) => ({ ...prev, employee_id: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Contact Number"
                value={userForm.contact_number}
                onChange={(e) => setUserForm((prev) => ({ ...prev, contact_number: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm((prev) => ({ ...prev, email: e.target.value }))}
                required
              />
              <FormControl fullWidth>
                <InputLabel id="user-role-label">Role</InputLabel>
                <Select
                  labelId="user-role-label"
                  label="Role"
                  value={userForm.role}
                  onChange={(e) => setUserForm((prev) => ({ ...prev, role: e.target.value }))}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.name}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeUserDialog} variant="text" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="user-form"
            variant="contained"
            disabled={saving || !roles.length}
            sx={{ textTransform: 'none', fontWeight: 700, minWidth: 140 }}
          >
            {saving ? 'Saving...' : userDialogMode === 'edit' ? 'Save Changes' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={credentialDialogOpen} onClose={() => setCredentialDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Temporary Password</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Copy this password now. It will not be shown again after you close this dialog.
          </Alert>
          <Stack spacing={1.75}>
            <TextField label="Email" value={createdCredentials?.email || ''} fullWidth InputProps={{ readOnly: true }} />
            <TextField
              label="Password"
              value={createdCredentials?.password || ''}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={async () => {
                        if (!createdCredentials?.password) return;
                        await navigator.clipboard.writeText(createdCredentials.password);
                        openSnack('Password copied.', 'success');
                      }}
                    >
                      <ContentCopyOutlined fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setCredentialDialogOpen(false);
              setCreatedCredentials(null);
            }}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteRoleTarget)} onClose={() => setDeleteRoleTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Delete Role</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#334155' }}>
            Delete <strong>{deleteRoleTarget?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
            Users assigned to this role must be reassigned before deletion.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteRoleTarget(null)} variant="text" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteRole} color="error" variant="contained" disabled={saving} sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteUserTarget)} onClose={() => setDeleteUserTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Delete User</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#334155' }}>
            Delete <strong>{deleteUserTarget?.full_name || deleteUserTarget?.email}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
            This removes the user from role access immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteUserTarget(null)} variant="text" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={saving} sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          variant="filled"
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </AuthShell>
  );
}
